import React, { Fragment, useMemo } from 'react';
import T from 'prop-types';

import skeletons from './Skeletons';
import Button from '../../../../Buttons/Button';
import { SmartChart } from '../../../../Chart';
import RatingCountries from '../../../../RatingCountries';
import defaultLineChartConfig from '../../../../Chart/LineChart';
import ErrorStateWithReload from '../../../../ErrorStateWithReload';
import MetricStats from '../../../../../pages/app-insights/_shared/components/MetricStats';
import InsightsCardBox from '../../../../../pages/app-insights/_shared/components/InsightsCardBox';

import links from '../../../configs/links';
import helpers from '../../../../../utils/helpers';
import { format } from '../../../../../utils/date';
import { numberToHumanChart } from '../../../../../utils/formatter';
import { tooltipRowTemplate } from '../../../../../utils/charts/chart-tooltips';

const emptyArray = [];
const emptyObject = {};
const metric = 'downloads';

const valueFormatter = (value, metricName) => {
  let newValue;
  if (metricName.indexOf('_revenue') > -1 || metricName === 'arpu') {
    newValue = `$${value < 1 ? value : numberToHumanChart(value)}`;
  } else if (metricName === 'eng') {
    newValue = `${value < 1 ? value : numberToHumanChart(value)}%`;
  } else {
    newValue = `${value < 1 ? value : numberToHumanChart(value)}`;
  }
  return `${newValue}`;
};

const chartConfig = (previous_period_data) =>
  defaultLineChartConfig({
    chartOptions: {
      height: 200,
      marginTop: 0,
      marginLeft: 0,
      marginRight: 0,
    },
    xAxisLabels: {
      fontSize: 10,
      color: '#848EA1',
      y: 20,
      formatter() {
        return Highcharts.dateFormat('%m/%d', this.value);
      },
    },
    xAxisOptions: {
      tickWidth: 0,
      tickLength: 0,
      gridLineWidth: 0,
      lineWidth: 0,
      tickInterval: 1000 * 60 * 60 * 24 * 7,
    },
    yAxisLabels: {
      enabled: false,
    },
    yAxisOptions: {
      tickAmount: 3,
      gridLineWidth: 0,
      visible: false,
      lineWidth: 0,
      offset: 0,
      floor: 0,
    },
    tooltipOptions: {
      borderWidth: 0,
      padding: 0,
      shared: true,
      valueDecimals: 2,
      useHTML: true,
      outside: true,
      className: 'quick-view-tooltip',
      formatter() {
        let rows;

        if (Array.isArray(this.points)) {
          rows = this.points.map((point) => {
            const formattedPoint = {
              color: point.color,
              backgroundColor: `${point.color === '#15385B' ? '#fff' : point.series.color}`,
              name: format(
                point.color !== '#15385B' ? point.x : previous_period_data[point.point.index][0],
                'MMM DD, YYYY'
              ),
              y: `${valueFormatter(point.y, metric)}`,
            };
            return tooltipRowTemplate(formattedPoint);
          });
        } else return false;

        return `
        <div
          style="
            min-width: 189px;
            border-radius: 8px;
            border: 1px solid #E1E5EB;
            padding: 16px;
            background: #fff;
            box-shadow: 0px 0px 24px 0px rgba(0, 0, 0, 0.08);
          "
        >
          <div style="
          font-size: 16px;
          font-style: normal;
          font-weight: 700;
          line-height: 150%;
          margin-bottom: 16px"
          class="text-black">
          Downloads
          </div>
          <table class="table">${rows.join('')}</table>
        </div>
      `;
      },
    },
  });

const metaSeriesData = {
  current_period_data: {
    checked: true,
    color: '#348BE3',
    name: 'Downloads',
    visible: true,
    type: 'areaspline',
    fillColor: {
      linearGradient: {
        x1: 0,
        y1: 0,
        x2: 0,
        y2: 1,
      },
      stops: [
        [0, helpers.hexToRGB('#348BE3', 0.2)],
        [1, helpers.hexToRGB('#348BE3', 0.01)],
      ],
    },
  },
  prev_period_data: {
    name: 'Previous',
    checked: true,
    color: '#15385B',
    visible: true,
    dashStyle: 'ShortDash',
    className: 'dashed-line',
    type: 'spline',
  },
};

const Performance = ({ performance, store, ref_no, settings }) => {
  const activeChartData = performance.time_series?.downloads || emptyObject;
  const dataRating = performance.country_breakdown?.downloads || emptyArray;
  const showData = !settings.pending;
  const series = useMemo(() => {
    const { current_period_data = [], previous_period_data = [] } = activeChartData;
    return {
      current_period_data,
      prev_period_data: current_period_data.map((current, i) => [
        current[0],
        previous_period_data[i][1],
      ]),
    };
  }, [activeChartData]);

  if (settings.error) return <ErrorStateWithReload />;

  return (
    <InsightsCardBox
      headerLeft={<div className="paragraph">Downloads</div>}
      headerRight={
        <Button variant="secondary" size="sm" href={links.performance(store, ref_no)}>
          Go to Performance
        </Button>
      }
      bodyMinHeight={160}
      className="performance-section"
    >
      <div className="performance-section-chart">
        {!showData && skeletons.performanceChart}
        {showData && (
          <Fragment>
            <MetricStats
              metric="downloads"
              change={activeChartData.change}
              total={activeChartData.total}
              type="estimated"
            />
            <SmartChart
              chartId="performance-section-chart"
              series={series}
              metaSeriesData={metaSeriesData}
              config={chartConfig(activeChartData.previous_period_data)}
            />
          </Fragment>
        )}
      </div>
      <div className="performance-section-rating">
        {showData ? (
          <RatingCountries countries={dataRating} metric="downloads_change" count={3} />
        ) : (
          skeletons.ratingsCountries
        )}
      </div>
    </InsightsCardBox>
  );
};
Performance.propTypes = {
  store: T.string,
  ref_no: T.oneOfType([T.string, T.number]),
  performance: T.object.isRequired,
  settings: T.object.isRequired,
};
export default Performance;
